import React, { useEffect, useState } from 'react';
import {
  Chip,
  Container,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { useTheme } from '@mui/material/styles';
import { getPosOrderSummary} from '../../../redux/slices/posRedux/posRedux';
import { fCurrency, fCurrencyWithoutSymbol } from '../../../utils/formatNumber';
import { tableCellCenterStyle, tableCellStyle, tableHeaderStyle, topicStyling } from '../../../utils/cssStyles';
import {
  accessVerify,
  capitalize,
  safeJSONParse,
  utcMoment,
  utcMomentDifferenceInMinutes,
} from '../../../utils/common';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { handleRePrint } from './components/posReprinter';
import PermissionRequired from '../../errorPages/permissionRequired';
import { getPaymentTypes } from '../../../redux/slices/financeRedux/paymentTypeRedux';

export default function PosOrderSummary() {
  const theme = useTheme();
  const dataModel = {
    id: null,
    name: null,
    description: null,
  };
  const tableRowStyle = {
    borderBottom: '1px solid #ddd',
  };

  const textTitleStyle = {
    fontSize: '12px',
    color: 'grey',
    marginRight: '10px',
  };

  const textInformationStyle = {
    fontSize: '15px',
    fontWeight: 500,
    paddingLeft: '15px',
  };

  const posLogStyle = {
    fontSize: '11px',
    borderBottom: '1px solid #bbbbbb',
    padding: '3px',
  };

  const hrLine = {
    borderBottom: '1px solid #555555',
    margin: '0px auto 20px auto',
    width: '100%',
  };

  const maxRows = 500;
  const { themeStretch } = useSettings();
  const [isShowAll, setIsShowAll] = useState(false);
  const [dataList, setDataList] = useState([]);
  const { selectedLocation } = useSelector((state) => state?.location);
  const { orderSummarydata: data } = useSelector((state) => state.pos);


  useEffect(() => {
    dispatch(getPosOrderSummary(selectedLocation?.id, isShowAll ? -1 : maxRows));
  }, [selectedLocation, isShowAll]);


  useEffect(() => {
    if (data?.length > 0) {
      setDataList(
        data?.map((value) => {
          return {
            ...value,
              orderDate: utcMoment(value?.orderTime).format('YYYY MM DD'),
              orderTime: utcMoment(value?.orderTime).format('HH:mm:ss'),
              paymentModes: (JSON.parse(value?.paymentModes) || []).join(', '),
          };
        })
      );
    } else {
      setDataList([]);
    }
  }, [data]);

  const getStatusColorCode = (type) => {
    if (type === 'Preparing') {
      return '#CE9600';
    } else if (type === 'Prepared') {
      return '#FF0000';
    } else if (type === 'Served') {
      return '#00921D';
    } else if (type === 'Refunded') {
      return '#FF0000';
    } else {
      return '#585858';
    }
  };

    const columns = [
        {
            accessorKey: 'orderNo',
            header: 'Order No',
        },
        {
            accessorKey: 'orderDate',
            header: 'Order Date',
        },
        {
            accessorKey: 'orderTime',
            header: 'Order Time',
        },
        {
            accessorKey: 'orderType',
            header: 'Order Type',
        },
        {
            accessorKey: 'paymentModes',
            header: 'Payment Mode',
        },
        {
            accessorKey: 'locationName',
            header: 'Location',
        },
        {
            accessorKey: 'guestName',
            header: 'Guest Name',
        },
        {
            accessorKey: 'contactNumber',
            header: 'Contact Number',
        },

        /* -------- ITEM INFO -------- */

        {
            accessorKey: 'foodId',
            header: 'Food ID',
        },
        {
            accessorKey: 'foodName',
            header: 'Food Name',
        },
        {
            accessorKey: 'foodCategory',
            header: 'Food Category',
        },
        {
            accessorKey: 'qty',
            header: 'Qty',
        },

        /* -------- PRICE INFO -------- */

        {
            accessorKey: 'grossPrice',
            header: 'Gross Price',
            Cell: ({ row }) => (
                <>{fCurrency(row.original.grossPrice)}</>
            ),
        },
        {
            accessorKey: 'itemDiscount',
            header: 'Discount',
            Cell: ({ row }) => (
                <>{fCurrency(row.original.itemDiscount)}</>
            ),
        },
        {
            accessorKey: 'totalPrice',
            header: 'Total Price',
            Cell: ({ row }) => (
                <>{fCurrency(row.original.totalPrice)}</>
            ),
        },
    ];


  const [rowSelection, setRowSelection] = useState(false);

  return (
    <Page title={`Pos Order Summary`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Pos Order Summary`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Sale',
              href: '',
            },
              { name: `Pos Order Summary` },
          ]}
        />
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <Stack direction='row' alignItems='center'>
              <Typography variant='overline' sx={{ mr: 1.5 }}>
                Get All Data
              </Typography>
              <Switch
                checked={isShowAll}
                onChange={(e) => {
                  setIsShowAll(e.target.checked);
                }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>

          </Grid>
        </Grid>
        {accessVerify('POS_ORDER_SUMMARY_PAGE') ? (
          <DataGridTable
            name={'POS Order Summary'}
            data={dataList}
            column={columns}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowActions={false}
            enablePinning={false}
            enableRowSelection={false}
            isRowClickable={true}
            renderRowActionItems={(value, closeMenu) => []}
          />
        ) : (
          <PermissionRequired />
        )}
      </Container>
    </Page>
  );
}
